import { createAuthRequestInit } from '../utils/auth';
import { API_BASE } from '../config/api';

export type SmsProviderType = 'econet' | 'kannel';

export interface SmsProviderDto {
  id: string;
  name: string;
  type: SmsProviderType;
  apiUrl: string;
  apiKeyMasked: string;
  senderField?: string | null;
  maxBatchSize: number;
  config?: Record<string, unknown> | null;
  username?: string;
  smsc?: string;
  dlrCallbackUrl?: string | null;
  priority: number;
  isActive: boolean;
  isDefault: boolean;
  createdBy?: string | null;
  createdAt: string;
  updatedAt: string;
  prefixes: string[];
}

export interface SmsProviderPayload {
  name: string;
  type: SmsProviderType;
  apiUrl: string;
  apiKey?: string;
  senderField?: string | null;
  maxBatchSize?: number;
  config?: Record<string, unknown> | null;
  username?: string;
  smsc?: string;
  priority?: number;
  isActive?: boolean;
  isDefault?: boolean;
  prefixes?: string[];
}

export interface SmsProviderTestResult {
  success: boolean;
  providerMessageId?: string;
  clientSmsId?: string;
  error?: string;
}

async function parseError(res: Response, fallback: string): Promise<string> {
  try {
    const json = await res.json();
    if (json?.message) return String(json.message);
  } catch (_) {
    // ignore
  }
  return fallback;
}

export async function getSmsProviders(): Promise<SmsProviderDto[]> {
  const res = await fetch(`${API_BASE}/sms-providers`, createAuthRequestInit());
  if (!res.ok) throw new Error(await parseError(res, 'Failed to fetch SMS providers'));
  const json = await res.json();
  return json.data.providers as SmsProviderDto[];
}

export async function createSmsProvider(payload: SmsProviderPayload): Promise<SmsProviderDto> {
  const res = await fetch(
    `${API_BASE}/sms-providers`,
    createAuthRequestInit(
      {
        method: 'POST',
        body: JSON.stringify(payload)
      },
      true
    )
  );
  if (!res.ok) throw new Error(await parseError(res, 'Failed to create SMS provider'));
  const json = await res.json();
  return json.data.provider as SmsProviderDto;
}

export async function updateSmsProvider(id: string, payload: Partial<SmsProviderPayload>): Promise<SmsProviderDto> {
  const res = await fetch(
    `${API_BASE}/sms-providers/${encodeURIComponent(id)}`,
    createAuthRequestInit(
      {
        method: 'PUT',
        body: JSON.stringify(payload)
      },
      true
    )
  );
  if (!res.ok) throw new Error(await parseError(res, 'Failed to update SMS provider'));
  const json = await res.json();
  return json.data.provider as SmsProviderDto;
}

export async function deleteSmsProvider(id: string): Promise<void> {
  const res = await fetch(
    `${API_BASE}/sms-providers/${encodeURIComponent(id)}`,
    createAuthRequestInit({
      method: 'DELETE'
    })
  );
  if (!res.ok && res.status !== 204) {
    throw new Error(await parseError(res, 'Failed to delete SMS provider'));
  }
}

export async function testSmsProvider(
  id: string,
  payload: { phone: string; senderId: string; body?: string }
): Promise<SmsProviderTestResult> {
  const res = await fetch(
    `${API_BASE}/sms-providers/${encodeURIComponent(id)}/test`,
    createAuthRequestInit(
      {
        method: 'POST',
        body: JSON.stringify(payload)
      },
      true
    )
  );
  if (!res.ok) throw new Error(await parseError(res, 'Failed to send test SMS'));
  const json = await res.json();
  return json.data as SmsProviderTestResult;
}
